/**
 * GiftNotificationWatcher - Watches for new gifts and shows toast notifications
 *
 * This is a headless component that monitors the unread gift count
 * and shows a toast notification when a new gift is received.
 * It should be placed in the app layout to run globally.
 */

import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Gift } from 'lucide-react';
import { useReceivedGifts } from '@/hooks/useReceivedGifts';

export function GiftNotificationWatcher() {
  const { gifts, unreadCount } = useReceivedGifts();
  const navigate = useNavigate();
  const previousCountRef = useRef<number | null>(null);
  const initialLoadRef = useRef(true);

  useEffect(() => {
    // Skip the initial load to avoid showing toast on page refresh
    if (initialLoadRef.current) {
      initialLoadRef.current = false;
      previousCountRef.current = unreadCount;
      return;
    }

    // Only show toast if count increased (new gift received)
    if (
      previousCountRef.current !== null &&
      unreadCount > previousCountRef.current
    ) {
      // Find the most recent unviewed gift
      const newGift = gifts.find((g) => !g.gift_viewed);
      const senderName = newGift?.sender_name || 'Someone';

      toast.success('New Gift Received!', {
        description: `${senderName} sent you a meditation gift`,
        icon: <Gift className="h-4 w-4 text-pink-500" />,
        action: {
          label: 'View Gift',
          onClick: () => navigate('/gifts?tab=received'),
        },
        duration: 8000, // Show for 8 seconds
      });
    }

    previousCountRef.current = unreadCount;
  }, [unreadCount, gifts, navigate]);

  // This is a watcher component, no UI to render
  return null;
}

export default GiftNotificationWatcher;
